import { useMemo } from "react";

import {
  type DataAssetTreeDirectoryResponse,
  type DataAssetWithManifestAndRealAssetDeploymentResponse,
} from "@/apiClient";
import { getBrowseItemIcon } from "@/components/dataBrowse/utils";

import { toFileSystemItems } from "./folderConfig.utils";

function ExpandedDirectoryContents({
  rootPath,
  observedSubdirectories,
  observedFiles,
  isLoading,
  isEmpty,
}: {
  rootPath: string;
  observedSubdirectories: DataAssetTreeDirectoryResponse[];
  observedFiles: DataAssetWithManifestAndRealAssetDeploymentResponse[];
  isLoading: boolean;
  isEmpty: boolean;
}) {
  const items = useMemo(
    () => toFileSystemItems(observedSubdirectories, observedFiles),
    [observedSubdirectories, observedFiles]
  );

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {isLoading ? (
        <p className="px-3 py-2 text-xs text-grey-200">Loading contents...</p>
      ) : isEmpty ? (
        <p className="px-3 py-2 text-xs text-grey-200">Empty at this level.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="shrink-0">
              {getBrowseItemIcon(
                { type: "folder", name: rootPath },
                {
                  folderSize: 15,
                  folderClassName: "text-yellow-500",
                }
              )}
            </div>
            <p className="min-w-0 truncate text-[13px] text-blue-400">
              {rootPath}
            </p>
          </div>
          <div className="border-t border-slate-100 pl-5">
            {items.map((item, index) => (
              <div
                key={item.path}
                className={`flex items-center gap-2 px-3 py-2 ${
                  index > 0 ? "border-t border-slate-100" : ""
                }`}
              >
                <div className="shrink-0">
                  {getBrowseItemIcon(item, {
                    folderSize: 15,
                    folderClassName: "text-yellow-500",
                  })}
                </div>
                <p className="min-w-0 truncate text-[13px] text-blue-400">
                  {item.name}
                  {item.type === "folder" ? "/" : ""}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ExpandedDirectoryContents;
